import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, CheckCircle2, BedDouble, PartyPopper, FileText } from "lucide-react";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import { ConsentNote } from "../components/legal/LegalBits";
import { quoteRequest } from "../lib/quotesApi";

const input = "w-full rounded-xl border border-[#e8dcc8] bg-white px-3 py-2.5 text-sm text-[#2b1410] focus:outline-none focus:ring-2 focus:ring-[#C48B31]/40";

export default function QuoteRequestPage() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("stay");
  const [f, setF] = useState({ name: "", phone: "", email: "", date_from: "", date_to: "", guests: 2, occasion: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await quoteRequest({ kind, ...f, guests: Number(f.guests) || 1 });
      setDone(r.number);
    } catch (err) {
      toast({ title: "Invio non riuscito", description: err?.response?.data?.detail || "Riprova tra poco", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#f7ebdf] text-[#2b1410]" data-testid="quote-request-page">
      <header className="sticky top-0 z-20 bg-[#4a1c17] text-[#f7ebdf]">
        <div className="mx-auto max-w-2xl px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm" data-testid="quote-request-back"><ChevronLeft className="h-4 w-4" /> Indietro</button>
          <div className="font-serif text-lg font-bold">Richiedi un preventivo</div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        <section className="relative overflow-hidden rounded-3xl bg-[#2b1410] text-[#f7ebdf] min-h-[220px] flex items-end shadow-lg">
          <video className="absolute inset-0 w-full h-full object-cover" src={kind === "stay" ? "/media/hero-rooms.mp4" : "/media/hero-event-friends.mp4"} poster={kind === "stay" ? "/media/hero-rooms-poster.jpg" : "/media/hero-event-friends-poster.jpg"} autoPlay loop muted playsInline aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410]/95 via-[#2b1410]/40 to-transparent" />
          <div className="relative p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#f7ebdf] bg-[#4a1c17]/70 backdrop-blur rounded-full px-2.5 py-1 inline-flex items-center gap-2"><FileText className="h-3.5 w-3.5" /> Su misura per te</div>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-bold leading-tight">Raccontaci cosa sogni, pensiamo noi al resto</h1>
            <p className="mt-2 text-sm text-[#f7ebdf]/90 max-w-md">Ti rispondiamo con un preventivo personalizzato, di solito entro poche ore.</p>
          </div>
        </section>

        {done ? (
          <div className="mt-6 rounded-2xl bg-white border border-[#e8dcc8] p-6 text-center" data-testid="quote-request-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h2 className="mt-3 font-serif text-2xl font-bold">Richiesta ricevuta!</h2>
            <p className="mt-2 text-sm text-[#7c5a4e]">Numero <b>{done}</b>. Ti scriviamo su WhatsApp{f.email ? " o via email" : ""} con la proposta. Grazie {f.name.split(" ")[0]}!</p>
            <button onClick={() => navigate("/")} className="mt-5 rounded-full bg-[#4a1c17] text-white px-6 py-3 text-sm font-semibold" data-testid="quote-request-home">Torna al Pozzo</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 rounded-2xl bg-white border border-[#e8dcc8] p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {[["stay", "Soggiorno", BedDouble, "Camere, colazione, Spa, cene, cavallo"], ["event", "Evento al ristorante", PartyPopper, "Cerimonie, compleanni, gruppi, aziende"]].map(([k, label, Icon, sub]) => (
                <button type="button" key={k} onClick={() => setKind(k)} className={`rounded-2xl border p-3 text-left transition ${kind === k ? "border-[#4a1c17] bg-[#4a1c17] text-white" : "border-[#e8dcc8] bg-[#faf3ee] text-[#2b1410]"}`} data-testid={`quote-kind-${k}`}>
                  <Icon className="h-5 w-5" />
                  <div className="mt-1 font-bold text-sm">{label}</div>
                  <div className={`text-[11px] ${kind === k ? "text-white/80" : "text-[#7c5a4e]"}`}>{sub}</div>
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder="Nome e cognome" value={f.name} onChange={set("name")} className={input} data-testid="quote-name" />
              <input required placeholder="Telefono (WhatsApp)" value={f.phone} onChange={set("phone")} className={input} data-testid="quote-phone" />
              <input type="email" placeholder="Email (facoltativa)" value={f.email} onChange={set("email")} className={`${input} sm:col-span-2`} data-testid="quote-email" />
              <label className="text-xs text-[#7c5a4e]">{kind === "stay" ? "Arrivo" : "Data evento"}<input type="date" value={f.date_from} onChange={set("date_from")} className={`${input} mt-1`} data-testid="quote-date-from" /></label>
              {kind === "stay" ? (
                <label className="text-xs text-[#7c5a4e]">Partenza<input type="date" value={f.date_to} onChange={set("date_to")} className={`${input} mt-1`} data-testid="quote-date-to" /></label>
              ) : (
                <label className="text-xs text-[#7c5a4e]">Occasione<input placeholder="es. battesimo, 50 anni, pranzo aziendale" value={f.occasion} onChange={set("occasion")} className={`${input} mt-1`} data-testid="quote-occasion" /></label>
              )}
              <label className="text-xs text-[#7c5a4e]">{kind === "stay" ? "Ospiti" : "Invitati (circa)"}<input type="number" min={1} value={f.guests} onChange={set("guests")} className={`${input} mt-1`} data-testid="quote-guests" /></label>
            </div>
            <textarea rows={4} placeholder={kind === "stay" ? "Es. 2 camere, cena romantica il sabato, massaggio di coppia, passeggiata a cavallo…" : "Es. pranzo per 40 persone con menu di carne, torta, allestimento in bianco e verde…"} value={f.text} onChange={set("text")} className={input} data-testid="quote-text" />
            <button type="submit" disabled={busy} className="w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="quote-request-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Invia la richiesta
            </button>
            <ConsentNote action="Inviando" />
          </form>
        )}
      </main>
      <Toaster />
    </div>
  );
}
